import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { usePerformanceGoals, useCreateGoal, useUpdateGoal } from "@/hooks/usePerformanceGoals";
import { usePerformanceIncentives, useCreateIncentive, useUpdateIncentive } from "@/hooks/usePerformanceIncentives";
import { Plus, Target, Award, Edit, CheckCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export default function PerformanceGoalsManagement() {
  const [isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);
  const [isIncentiveDialogOpen, setIsIncentiveDialogOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<any>(null);
  const [selectedIncentive, setSelectedIncentive] = useState<any>(null);

  const { data: goals } = usePerformanceGoals();
  const { data: incentives } = usePerformanceIncentives();
  const createGoal = useCreateGoal();
  const updateGoal = useUpdateGoal();
  const createIncentive = useCreateIncentive();
  const updateIncentive = useUpdateIncentive();
  const queryClient = useQueryClient();

  const [goalForm, setGoalForm] = useState({
    employee_id: "",
    goal_type: "sales",
    title: "",
    description: "",
    target_value: 0,
    current_value: 0,
    unit: "SAR",
    start_date: "",
    end_date: "",
    priority: "medium",
    incentive_amount: 0,
  });

  const [incentiveForm, setIncentiveForm] = useState({
    employee_id: "",
    incentive_type: "goal_achievement",
    title: "",
    description: "",
    amount: 0,
    currency: "SAR",
    status: "pending",
  });

  const handleCreateGoal = async () => {
    try {
      await createGoal.mutateAsync(goalForm);
      setIsGoalDialogOpen(false);
      setGoalForm({
        employee_id: "",
        goal_type: "sales",
        title: "",
        description: "",
        target_value: 0,
        current_value: 0,
        unit: "SAR",
        start_date: "",
        end_date: "",
        priority: "medium",
        incentive_amount: 0,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpdateGoalProgress = async (goalId: string, currentValue: number) => {
    try {
      await updateGoal.mutateAsync({
        id: goalId,
        data: { current_value: currentValue },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleApproveIncentive = async (incentiveId: string) => {
    try {
      await updateIncentive.mutateAsync({
        id: incentiveId,
        data: { status: "approved", approved_at: new Date().toISOString() },
      });
      toast.success("تم الموافقة على الحافز");
    } catch (error) {
      console.error(error);
    }
  };

  const activeGoals = goals?.filter((g) => g.status === "active") || [];
  const pendingIncentives = incentives?.filter((i) => i.status === "pending") || [];

  // رسالة عند عدم وجود بيانات
  const NoDataMessage = ({ title, description }: { title: string; description: string }) => (
    <Card>
      <CardContent className="p-12 text-center space-y-4">
        <Target className="h-16 w-16 mx-auto text-muted-foreground" />
        <h2 className="text-xl font-bold">{title}</h2>
        <p className="text-muted-foreground max-w-md mx-auto">{description}</p>
      </CardContent>
    </Card>
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "bg-green-500";
      case "active": return "bg-blue-500";
      case "cancelled": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">إدارة الأهداف والحوافز</h1>
        <div className="flex gap-2">
          <Dialog open={isGoalDialogOpen} onOpenChange={setIsGoalDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                هدف جديد
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>إنشاء هدف جديد</DialogTitle>
                <DialogDescription>أضف هدف أداء جديد للموظف</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>نوع الهدف</Label>
                  <Select
                    value={goalForm.goal_type}
                    onValueChange={(value) => setGoalForm({ ...goalForm, goal_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sales">مبيعات</SelectItem>
                      <SelectItem value="customer_service">خدمة العملاء</SelectItem>
                      <SelectItem value="productivity">إنتاجية</SelectItem>
                      <SelectItem value="attendance">حضور</SelectItem>
                      <SelectItem value="custom">مخصص</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>العنوان</Label>
                  <Input
                    value={goalForm.title}
                    onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                  />
                </div>
                <div>
                  <Label>الوصف</Label>
                  <Textarea
                    value={goalForm.description}
                    onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>القيمة المستهدفة</Label>
                    <Input
                      type="number"
                      value={goalForm.target_value}
                      onChange={(e) => setGoalForm({ ...goalForm, target_value: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>الوحدة</Label>
                    <Input
                      value={goalForm.unit}
                      onChange={(e) => setGoalForm({ ...goalForm, unit: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>تاريخ البداية</Label>
                    <Input
                      type="date"
                      value={goalForm.start_date}
                      onChange={(e) => setGoalForm({ ...goalForm, start_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>تاريخ النهاية</Label>
                    <Input
                      type="date"
                      value={goalForm.end_date}
                      onChange={(e) => setGoalForm({ ...goalForm, end_date: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>الحافز (اختياري)</Label>
                  <Input
                    type="number"
                    placeholder="المبلغ بالريال"
                    value={goalForm.incentive_amount}
                    onChange={(e) => setGoalForm({ ...goalForm, incentive_amount: Number(e.target.value) })}
                  />
                </div>
                <Button onClick={handleCreateGoal} className="w-full">
                  إنشاء الهدف
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="goals" className="space-y-4">
        <TabsList>
          <TabsTrigger value="goals">الأهداف النشطة</TabsTrigger>
          <TabsTrigger value="incentives">الحوافز المعلقة</TabsTrigger>
          <TabsTrigger value="all">جميع الأهداف</TabsTrigger>
        </TabsList>

        <TabsContent value="goals" className="space-y-4">
          {activeGoals.length === 0 ? (
            <NoDataMessage
              title="لا توجد أهداف نشطة"
              description="ابدأ بإنشاء أهداف جديدة للموظفين لتتبع أدائهم وتحفيزهم على تحقيق النتائج المطلوبة."
            />
          ) : (
            <div className="grid gap-4">
            {activeGoals.map((goal) => (
              <Card key={goal.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5" />
                        {goal.title}
                      </CardTitle>
                      <CardDescription>{goal.description}</CardDescription>
                      {goal.employees && (
                        <p className="text-sm text-muted-foreground mt-1">
                          الموظف: {goal.employees.name}
                        </p>
                      )}
                    </div>
                    <Badge className={getStatusColor(goal.status)}>
                      {goal.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>التقدم</span>
                      <span className="font-bold">{goal.progress_percentage}%</span>
                    </div>
                    <Progress value={goal.progress_percentage} />
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">القيمة الحالية: </span>
                      <span className="font-bold">{goal.current_value} {goal.unit}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">الهدف: </span>
                      <span className="font-bold">{goal.target_value} {goal.unit}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">البداية: </span>
                      <span className="font-bold">
                        {new Date(goal.start_date).toLocaleDateString("ar-SA")}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">النهاية: </span>
                      <span className="font-bold">
                        {new Date(goal.end_date).toLocaleDateString("ar-SA")}
                      </span>
                    </div>
                  </div>
                  {goal.incentive_amount && (
                    <div className="flex items-center gap-2 p-2 bg-green-50 rounded-md">
                      <Award className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-600">
                        حافز: {goal.incentive_amount} {goal.incentive_currency}
                      </span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const newValue = prompt("أدخل القيمة الحالية:");
                        if (newValue) {
                          handleUpdateGoalProgress(goal.id, Number(newValue));
                        }
                      }}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      تحديث التقدم
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          )}
        </TabsContent>

        <TabsContent value="incentives" className="space-y-4">
          {pendingIncentives.length === 0 ? (
            <NoDataMessage
              title="لا توجد حوافز معلقة"
              description="سيظهر هنا الحوافز التي تحتاج موافقة الإدارة. يتم إنشاء الحوافز تلقائياً عند إتمام الأهداف."
            />
          ) : (
            <div className="grid gap-4">
            {pendingIncentives.map((incentive) => (
              <Card key={incentive.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{incentive.title}</CardTitle>
                      <CardDescription>{incentive.description}</CardDescription>
                      {incentive.employees && (
                        <p className="text-sm text-muted-foreground mt-1">
                          الموظف: {incentive.employees.name}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline">{incentive.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold">
                      {incentive.amount} {incentive.currency}
                    </span>
                    <Badge>{incentive.incentive_type}</Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleApproveIncentive(incentive.id)}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      الموافقة
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          )}
        </TabsContent>

        <TabsContent value="all" className="space-y-4">
          {!goals || goals.length === 0 ? (
            <NoDataMessage
              title="لا توجد أهداف"
              description="لم يتم إنشاء أي أهداف بعد. استخدم زر 'هدف جديد' لإضافة أهداف للموظفين."
            />
          ) : (
            <div className="grid gap-4">
            {goals?.map((goal) => (
              <Card key={goal.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle>{goal.title}</CardTitle>
                    <Badge className={getStatusColor(goal.status)}>
                      {goal.status}
                    </Badge>
                  </div>
                  {goal.employees && (
                    <CardDescription>الموظف: {goal.employees.name}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Progress value={goal.progress_percentage} />
                    <p className="text-sm text-muted-foreground">
                      {goal.current_value} / {goal.target_value} {goal.unit}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}